import React, { useEffect, useState } from "react";
import SimpleBar from "simplebar-react";
import { useLocation, Link } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faBoxOpen,
  faChartPie,
  faCog,
  faFileAlt,
  faHandHoldingUsd,
  faSignOutAlt,
  faTable,
  faTimes,
  faCalendarAlt,
  faMapPin,
  faInbox,
  faRocket,
  faBookOpen,
  faShoppingCart,
  faUsers,
  faCoins,
  faBookAtlas,
  faPaperPlane,
  faFileInvoiceDollar,
  faSitemap,
  faWallet,
  faMoneyBillTransfer,
  faChartLine,
  faCheckCircle,
  faHandHoldingDollar,
  faChartColumn,
  faHistory,
  faTrophy,
  faMedal,
  faFileInvoice,
  faClipboardList,
  faFileLines,
  faQuestionCircle,
  faUndo,
  faIdCard,
  faChevronRight,
  faChevronDown,
  faChevronLeft,
  faListAlt,
} from "@fortawesome/free-solid-svg-icons";
import {
  Nav,
  Badge,
  Image,
  Button,
  Dropdown,
  Accordion,
  Navbar,
} from "@themesberg/react-bootstrap";

import { Routes } from "../routes";
import ThemesbergLogo from "../assets/img/themesberg.svg";
// import SidebarLogo from "../assets/img/sidebar/LSG_Logo_8K_Sidebar.png";
import SidebarLogo from "../assets/img/sidebar/LSG_Logo_Sidebar.png";
import ProfilePicture from "../assets/img/team/profile-picture-3.jpg";
import "./css/Sidebar.css";

const getStoredUser = () => {
  try {
    const raw = sessionStorage.getItem("authUser");
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const getStoredRole = () => {
  const user = getStoredUser();
  return String(user?.user_type || sessionStorage.getItem("authRole") || "").trim().toUpperCase();
};

const isAdminRole = () => {
  const role = getStoredRole();
  return ["ADMIN", "SUPER_ADMIN", "ROLE_ADMIN", "ROLE_SUPER_ADMIN"].includes(role);
};

const isUserRole = () => {
  const role = getStoredRole();
  return ["USER", "ROLE_USER", "CHILD", "ROLE_CHILD"].includes(role);
};

const isPrimaryAccount = () => {
  const user = getStoredUser();
  return !user?.is_sub_id;
};

export default (props = {}) => {
  const location = useLocation();
  const { pathname } = location;
  const [show, setShow] = useState(false);
  const showClass = show ? "show" : "";

  const onCollapse = () => setShow(!show);

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    sessionStorage.removeItem("authTokenType");
    sessionStorage.removeItem("authUser");
    sessionStorage.removeItem("authRole");
    sessionStorage.removeItem("isAuthenticated");
    sessionStorage.removeItem("authProfilePhoto");
    window.location.href = Routes.Signin.path;
  };

  const isPathMatch = (link) => {
    if (!link) return false;
    return pathname === link || pathname.startsWith(`${link}/`);
  };

  const CollapsableNavItem = (props) => {
    const { eventKey, title, icon, children = null, openPaths = [] } = props;
    const pathOpen = openPaths.some((p) => isPathMatch(p));
    const [expandedKey, setExpandedKey] = useState(pathOpen ? eventKey : "");

    useEffect(() => {
      setExpandedKey(pathOpen ? eventKey : "");
    }, [pathname, pathOpen, eventKey]);

    return (
      <Accordion
        as={Nav.Item}
        activeKey={expandedKey}
        onSelect={(key) => setExpandedKey(key || "")}
        className={`sidebar-accordion${pathOpen ? " is-section-active" : ""}`}
      >
        <Accordion.Item eventKey={eventKey} className="border-0">
          <Accordion.Button
            as={Nav.Link}
            className={`sidebar-link sidebar-collapsible ${pathOpen ? "is-active" : ""}`}
          >
            <div className="sidebar-collapse-content">
              <div className="sidebar-collapse-left">
                <span className="sidebar-icon">
                  <FontAwesomeIcon icon={icon} />
                </span>

                <span className="sidebar-text">{title}</span>
              </div>

              <FontAwesomeIcon
                icon={expandedKey === eventKey ? faChevronDown : faChevronRight}
                className="sidebar-collapse-icon"
              />
            </div>
          </Accordion.Button>
          <Accordion.Body className="multi-level pt-0">
            <Nav className="flex-column sidebar-subnav">{children}</Nav>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    );
  };

  const NavItem = (props) => {
    const {
      title,
      link,
      external,
      target,
      icon,
      image,
      badgeText,
      badgeBg = "secondary",
      badgeColor = "primary",
    } = props;

    const isActive = !external && isPathMatch(link);
    const classNames = badgeText
      ? "sidebar-link d-flex justify-content-between align-items-center"
      : "sidebar-link";
    const linkProps = external ? { href: link } : { as: Link, to: link };

    return (
      <Nav.Item className={`sidebar-item ${isActive ? "active" : ""}`} onClick={() => setShow(false)}>
        <Nav.Link {...linkProps} target={target} className={classNames}>
          <span className="d-flex align-items-center">
            {icon ? (
              <span className="sidebar-icon">
                <FontAwesomeIcon icon={icon} />
              </span>
            ) : null}
            {image ? (
              <Image src={image} width={20} height={20} className="sidebar-icon svg-icon" />
            ) : null}
            <span className="sidebar-text">{title}</span>
          </span>

          {badgeText ? (
            <Badge pill bg={badgeBg} text={badgeColor} className="badge-md ms-2">
              {badgeText}
            </Badge>
          ) : null}
        </Nav.Link>
      </Nav.Item>
    );
  };

  const adminMenu = isAdminRole();
  const userMenu = isUserRole() || !adminMenu;
  const storedUser = getStoredUser();
  const sidebarDisplayName = storedUser?.name?.trim() || (adminMenu ? "Admin" : "User");

  return (
    <>
      <Navbar expand={false} collapseOnSelect className="sidebar-topbar px-4 d-md-none">
        <div className="d-flex align-items-center w-100">
          <Navbar.Brand className="me-auto" as={Link} to={adminMenu ? Routes.AdminDashboard?.path : Routes.UserDashboard?.path}>
            <Image src={SidebarLogo} alt="Logo" />
          </Navbar.Brand>

          <Navbar.Toggle as={Button} aria-controls="main-navbar" onClick={onCollapse}>
            <span className="navbar-toggler-icon" />
          </Navbar.Toggle>
        </div>
      </Navbar>

      <CSSTransition timeout={300} in={show} classNames="sidebar-transition">
        <SimpleBar className={`collapse ${showClass} sidebar d-md-block sidebar-shell`}>
          <div className="sidebar-inner px-3 py-3">
            <div className="sidebar-brand-box">
              <div className="d-flex align-items-center w-100">
                <div className="sidebar-brand-icon mb-1">
                  <Image src={SidebarLogo} alt="Logo" className="w-100 h-100" />
                </div>

                <div className="ms-3 min-w-0 flex-grow-1">
                  <h5 className="mb-0 sidebar-brand-title text-truncate" title={sidebarDisplayName}>
                    {sidebarDisplayName}
                  </h5>
                  <div className="sidebar-brand-subtitle">
                    {adminMenu ? "Admin Dashboard" : "User Dashboard"}
                  </div>
                </div>

                {show && (
                  <Nav.Link className="collapse-close d-md-none ms-auto" onClick={onCollapse}>
                    <FontAwesomeIcon icon={faTimes} />
                  </Nav.Link>
                )}
              </div>
            </div>

            {/* <div className="user-card d-flex d-md-none align-items-center justify-content-between pt-3 pb-3">
              <div className="d-flex align-items-center">
                <div className="user-avatar lg-avatar me-3">
                  <Image src={ProfilePicture} className="card-img-top rounded-circle border-white" />
                </div>
                <div className="d-block">
                  <h6 className="mb-0">Hi, Jane</h6>
                  <small className="text-muted d-block">Partner Account</small>
                </div>
              </div>
              <Nav.Link className="collapse-close d-md-none" onClick={onCollapse}>
                <FontAwesomeIcon icon={faTimes} />
              </Nav.Link>
            </div> */}

            <Nav className="flex-column sidebar-nav pt-2 pt-md-3">
              {adminMenu && (
                <>
                  <NavItem title="Dashboard" link={Routes.AdminDashboard.path} icon={faChartPie} />
                  <NavItem title="User Management" link={Routes.AdminUserManagement.path} icon={faUsers} />
                  <NavItem title="Courses" link={Routes.AdminCourseList.path} icon={faBookAtlas} />
                  <NavItem title="Notifications" link={Routes.AdminSendNotifications.path} icon={faPaperPlane} />
                  <NavItem title="Commission" link={Routes.AdminCommissionList.path} icon={faFileInvoiceDollar} />
                  <NavItem title="Bonus Management" link={Routes.AdminBonusDashboard.path} icon={faTrophy} />
                  <NavItem title="Bonus History" link={Routes.AdminBonusHistory.path} icon={faHistory} />
                  <NavItem title="Payment Confirmations" link={Routes.AdminPaymentConfirmList.path} icon={faCheckCircle} />
                  <NavItem title="Transaction History" link={Routes.AdminTransactionHistory.path} icon={faFileAlt} />
                  {/* <NavItem title="Withdrawal Requests" link={Routes.AdminWithdrawalRequests.path} icon={faHandHoldingDollar} /> */}
                  {/* <NavItem title="Settlement History" link={Routes.AdminSettlementHistory.path} icon={faMoneyBillTransfer} /> */}
                  <NavItem title="Earning Statement" link={Routes.AdminEarningStatement.path} icon={faClipboardList} />
                  <NavItem title="Refund Management" link={Routes.AdminRefundManagement.path} icon={faUndo} />
                  <NavItem title="Support" link={Routes.AdminSupportTicketList.path} icon={faQuestionCircle} />
                  <NavItem title="Logs" link={Routes.AdminLogs.path} icon={faListAlt} />
                  <NavItem title="Reports" link={Routes.AdminReports.path} icon={faChartLine} />
                  {/* <NavItem title="Level Earnings" link={Routes.AdminLevelEarnings.path} icon={faCoins} /> */}
                  {/* <NavItem title="Profile" link={Routes.AdminProfilePage.path} icon={faCog} /> */}
                </>
              )}

              {userMenu && (
                <>
                  <NavItem title="Dashboard" link={Routes.UserDashboard.path} icon={faChartPie} />
                  {/* <NavItem title="My Courses" link={Routes.MyCourses?.path || "/my-courses"} icon={faBookOpen} />
                  <NavItem title="Purchase Courses" link={Routes.PurchaseCourses?.path || "/purchase-courses"} icon={faShoppingCart} /> */}
                  {/* <NavItem title="Purchase Courses" link={Routes.UserPurchaseCourseList.path} icon={faShoppingCart} /> */}

                  {isPrimaryAccount() && (
                    <NavItem title="My Team" link={Routes.UserMyTeam.path} icon={faIdCard} />
                  )}

                  <CollapsableNavItem
                    eventKey="course-management"
                    title="Course Management"
                    icon={faBookOpen}
                    openPaths={[
                      Routes.UserMyCourseList.path,
                      Routes.UserPurchaseCourseList.path,
                      Routes.UserCollaborativePurchase.path,
                    ]}
                  >
                    <NavItem title="My Courses" link={Routes.UserMyCourseList.path} />
                    <NavItem title="Purchase Course" link={Routes.UserPurchaseCourseList.path} />
                    {isPrimaryAccount() && (
                      <NavItem title="Group Purchase" link={Routes.UserCollaborativePurchase.path} />
                    )}
                  </CollapsableNavItem>
                  {/* <NavItem title="My Courses" link={Routes.UserMyCourseList.path} icon={faBookOpen} /> */}
                  <NavItem title="Level Earnings" link={Routes.UserLevelEarning.path} icon={faCoins} />
                  <NavItem title="Network View" link={Routes.UserLevelNetworkPage.path} icon={faSitemap} />
                  <NavItem title="My Bonus" link={Routes.UserMyBonus.path} icon={faMedal} />
                  <NavItem title="My Wallet" link={Routes.UserWalletPage.path} icon={faWallet} />
                  <CollapsableNavItem
                    eventKey="payments"
                    title="Payments"
                    icon={faFileInvoiceDollar}
                    openPaths={[
                      Routes.UserMyPayments.path,
                      Routes.UserInvoices.path,
                      Routes.UserCreditNotes.path,
                      Routes.UserGst.path,
                      Routes.UserTds.path,
                    ]}
                  >
                    <NavItem title="Payment History" link={Routes.UserMyPayments.path} />
                    <NavItem title="Invoices" link={Routes.UserInvoices.path} />
                    <NavItem title="Credit Notes" link={Routes.UserCreditNotes.path} />
                    <NavItem title="GST" link={Routes.UserGst.path} />
                    <NavItem title="TDS" link={Routes.UserTds.path} />
                  </CollapsableNavItem>
                  {/* <NavItem title="Withdrawal History" link={Routes.UserWithdrawalHistory.path} icon={faMoneyBillTransfer} />  */}
                  {/* <NavItem title="Settlement History" link={Routes.UserSettlementHistory.path} icon={faMoneyBillTransfer} /> */}
                  <NavItem title="Refund Management" link={Routes.UserRefundManagement.path} icon={faUndo} />
                  <NavItem title="Support" link={Routes.UserSupportTicketList.path} icon={faQuestionCircle} />
                  <NavItem title="Reports" link={Routes.UserReports.path} icon={faChartColumn} />
                  {/* <NavItem title="User Courses" link={Routes.UserPurchaseCourseList.path} icon={faBook} /> */}
                  {/* <NavItem title="Profile" link={Routes.UserProfilePage.path} icon={faCog} /> */}
                </>
              )}

              <Dropdown.Divider className="my-3 sidebar-divider" />

              {/* <NavItem title="Logout" link="#" icon={faSignOutAlt} external={true} onClick={handleLogout} /> */}

              {/* <NavItem title="My Users" link={Routes.Users?.path || "/users"} icon={faBook} />
              <NavItem title="Earnings" link={Routes.Earnings?.path || "/earnings"} icon={faHandHoldingUsd} />
              <NavItem title="Wallet" link={Routes.Wallet?.path || "/wallet"} icon={faBoxOpen} />
              <NavItem title="Withdrawals" link={Routes.Withdrawals?.path || "/withdrawals"} icon={faFileAlt} />
              <NavItem title="Profile" link={Routes.Profile?.path || "/profile"} icon={faCog} />

              <Dropdown.Divider className="my-3 sidebar-divider" />

              <CollapsableNavItem eventKey="tables/" title="Tables" icon={faTable}>
                <NavItem title="Bootstrap Table" link={Routes.BootstrapTables.path} />
              </CollapsableNavItem>

              <CollapsableNavItem eventKey="examples/" title="Page Examples" icon={faFileAlt}>
                <NavItem title="Sign In" link={Routes.Signin.path} />
                <NavItem title="Sign Up" link={Routes.Signup.path} />
                <NavItem title="Forgot password" link={Routes.ForgotPassword.path} />
                <NavItem title="Reset password" link={Routes.ResetPassword.path} />
                <NavItem title="Lock" link={Routes.Lock.path} />
                <NavItem title="404 Not Found" link={Routes.NotFound.path} />
                <NavItem title="500 Server Error" link={Routes.ServerError.path} />
              </CollapsableNavItem> */}

              {/* <NavItem
                external
                title="Messages"
                link="https://demo.themesberg.com/volt-pro-react/#/messages"
                target="_blank"
                badgeText="Pro"
                icon={faInbox}
              />
              <NavItem
                external
                title="Calendar"
                link="https://demo.themesberg.com/volt-pro-react/#/calendar"
                target="_blank"
                badgeText="Pro"
                icon={faCalendarAlt}
              />
              <NavItem
                external
                title="Map"
                link="https://demo.themesberg.com/volt-pro-react/#/map"
                target="_blank"
                badgeText="Pro"
                icon={faMapPin}
              />
              <NavItem
                external
                title="Plugins"
                link="https://demo.themesberg.com/volt-pro-react/#/plugins/datatable"
                target="_blank"
                badgeText="Pro"
                icon={faChartPie}
              />

              <Dropdown.Divider className="my-3 sidebar-divider" />

              <NavItem
                external
                title="Themesberg"
                link="https://themesberg.com"
                target="_blank"
                image={ThemesbergLogo}
              />

              <Button
                as={Link}
                to={Routes.Upgrade.path}
                variant="primary"
                className="upgrade-to-pro mt-3"
              >
                <FontAwesomeIcon icon={faRocket} className="me-2" />
                Upgrade to Pro
              </Button> */}
            </Nav>
          </div>
        </SimpleBar>
      </CSSTransition>
    </>
  );
};